import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { db } from '../firebase/config';
import {
  doc,
  getDoc,
  onSnapshot,
  updateDoc,
  setDoc,
  serverTimestamp,
  runTransaction,
  Timestamp,
  collection,
  writeBatch,
  query,
  where
} from 'firebase/firestore';
import { useAuth } from './AuthContext';

const BalanceContext = createContext();

// Default balances structure with trading, funding, arbitrage, and arbitrageReserved accounts.
// arbitrageReserved holds funds locked in active arbitrage positions — must be tracked
// separately so the UI can render "Locked" amounts and the available balance excludes them.
const DEFAULT_BALANCES = {
  accountBalances: {
    trading: {
      USDT: 0,
      USDC: 0,
      BTC: 0,
      ETH: 0,
      XRP: 0,
      SOL: 0
    },
    funding: {
      USDT: 0,
      USDC: 0,
      BTC: 0,
      ETH: 0,
      XRP: 0,
      SOL: 0
    },
    arbitrage: {
      USDT: 0,
      USDC: 0,
      BTC: 0,
      ETH: 0,
      XRP: 0,
      SOL: 0
    },
    arbitrageReserved: {
      USDT: 0,
      USDC: 0,
      BTC: 0,
      ETH: 0,
      XRP: 0,
      SOL: 0
    }
  },
  transactions: []
};

export const BalanceProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [balanceData, setBalanceData] = useState({
    ...DEFAULT_BALANCES,
    isLoading: false,
    error: null
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper function to get the correct coin key with case insensitivity
  const getCoinKey = useCallback((coin, balances = {}) => {
    if (!coin) return '';
    const upperCoin = coin.toUpperCase();
    return Object.keys(balances).find(k => k.toUpperCase() === upperCoin) || coin;
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setBalanceData(DEFAULT_BALANCES);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const userRef = doc(db, 'userProfiles', currentUser.uid);

    const setupListener = () => {
      const unsubscribe = onSnapshot(
        userRef,
        (doc) => {
          if (doc.exists()) {
            const userData = doc.data();
            const updatedBalances = {
              ...DEFAULT_BALANCES,
              accountBalances: {
                trading: {
                  ...DEFAULT_BALANCES.accountBalances.trading,
                  ...(userData.accountBalances?.trading || {}),
                },
                funding: {
                  ...DEFAULT_BALANCES.accountBalances.funding,
                  ...(userData.accountBalances?.funding || {}),
                },
                arbitrage: {
                  ...DEFAULT_BALANCES.accountBalances.arbitrage,
                  ...(userData.accountBalances?.arbitrage || {}),
                },
                // Critical: include arbitrageReserved so the "Locked" UI reflects
                // funds committed to active arbitrage positions in real time.
                arbitrageReserved: {
                  ...DEFAULT_BALANCES.accountBalances.arbitrageReserved,
                  ...(userData.accountBalances?.arbitrageReserved || {}),
                },
              },
              transactions: Array.isArray(userData.transactions) ? [...userData.transactions] : [],
            };
            setBalanceData(updatedBalances);
          } else {
            // This case should ideally not be hit if initialize runs first
            setBalanceData(DEFAULT_BALANCES);
          }
          setIsLoading(false);
        },
        (err) => {
          console.error('Error in balance listener:', err);
          setError(err.message || 'Failed to load balance data');
          setIsLoading(false);
        }
      );
      return unsubscribe;
    };

    const initializeAndListen = async () => {
      try {
        const docSnap = await getDoc(userRef);
        if (!docSnap.exists()) {
          console.log('User profile not found, creating one...');
          await setDoc(userRef, DEFAULT_BALANCES);
        }
        // Now that we're sure the doc exists, set up the listener
        return setupListener();
      } catch (err) {
        console.error('Error initializing user profile:', err);
        setError('Failed to initialize user profile');
        setIsLoading(false);
        return () => {}; // Return an empty unsubscribe function on error
      }
    };

    let unsubscribe;
    initializeAndListen().then(unsub => {
      unsubscribe = unsub;
    });

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser]);

  const updateBalance = useCallback(async (coin, amount, accountType = 'trading', note = '') => {
    if (!currentUser) return false;

    const userRef = doc(db, 'userProfiles', currentUser.uid);
    const rawCoinKey = getCoinKey(coin, balanceData.accountBalances[accountType]) || coin || '';
    const normalizedCoinKey = rawCoinKey.toUpperCase();
    const coinKeys = Array.from(new Set([normalizedCoinKey, rawCoinKey])).filter(Boolean);

    try {
      const payload = {
        updatedAt: serverTimestamp()
      };

      coinKeys.forEach((key) => {
        if (key) {
          payload[`accountBalances.${accountType}.${key}`] = amount;
        }
      });

      await updateDoc(userRef, payload);
      
      return true;
    } catch (error) {
      console.error('Error updating balance:', error);
      setError('Failed to update balance');
      return false;
    }
  }, [currentUser, balanceData, getCoinKey]);

  const applyBalanceDelta = useCallback(
    async (coin, deltaAmount, accountType = 'trading', note = '', type = 'adjustment', txStatus = 'completed', txRecordId = null) => {
      if (!currentUser) return false;

      const userRef = doc(db, 'userProfiles', currentUser.uid);
      const rawCoinKey = getCoinKey(coin, balanceData.accountBalances?.[accountType] || {}) || coin || '';
      const normalizedCoinKey = rawCoinKey.toUpperCase();
      const coinKeys = Array.from(new Set([normalizedCoinKey, rawCoinKey])).filter(Boolean);

      try {
        await runTransaction(db, async (transaction) => {
          const docSnap = await transaction.get(userRef);
          if (!docSnap.exists()) {
            throw new Error('User profile not found');
          }

          const userData = docSnap.data() || {};
          const existingBalances = userData.accountBalances?.[accountType] || {};
          const firstExistingKey = coinKeys.find((key) => existingBalances[key] !== undefined);
          const currentAmount = Number(firstExistingKey ? existingBalances[firstExistingKey] : 0);
          const nextAmount = Number((currentAmount + Number(deltaAmount || 0)).toFixed(8));

          const updates = {
            updatedAt: serverTimestamp()
          };

          coinKeys.forEach((key) => {
            if (key) {
              updates[`accountBalances.${accountType}.${key}`] = nextAmount;
            }
          });

          if (note) {
            const existingTransactions = Array.isArray(userData.transactions) ? userData.transactions : [];
            const processedTimestamp = Timestamp.fromDate(new Date());
            const adjustmentRecord = {
              id: txRecordId || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
              type,
              status: txStatus,
              coin: normalizedCoinKey,
              amount: Math.abs(Number(deltaAmount) || 0),
              accountType,
              direction: Number(deltaAmount || 0) >= 0 ? 'incoming' : 'outgoing',
              note,
              timestamp: processedTimestamp,
              processed: txStatus === 'completed',
              processedAt: txStatus === 'completed' ? processedTimestamp : null
            };

            updates.transactions = [adjustmentRecord, ...existingTransactions].slice(0, 100);
          }

          transaction.update(userRef, updates);
        });

        return true;
      } catch (error) {
        console.error('Error applying balance delta:', error);
        setError('Failed to apply balance update');
        return false;
      }
    },
    [currentUser, balanceData, getCoinKey]
  );

  const createPendingTransaction = useCallback(
    async ({
      type,
      coin,
      amount,
      accountType = 'trading',
      txHash = '',
      note = '',
      direction = 'incoming',
      destinationAddress = null
    }) => {
      if (!currentUser) return false;

      const userRef = doc(db, 'userProfiles', currentUser.uid);
      const rawCoinKey = getCoinKey(coin, balanceData.accountBalances?.[accountType] || {}) || coin || '';
      const normalizedCoinKey = rawCoinKey.toUpperCase();

      const generateTransactionId = () => {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
          return crypto.randomUUID();
        }
        return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      };

      try {
        const timestamp = Timestamp.fromDate(new Date());
        let createdTransaction = null;

        await runTransaction(db, async (transaction) => {
          const docSnap = await transaction.get(userRef);
          if (!docSnap.exists()) {
            throw new Error('User profile not found');
          }

          const userData = docSnap.data() || {};
          const existingTransactions = Array.isArray(userData.transactions) ? userData.transactions : [];

          const newTransaction = {
            id: generateTransactionId(),
            type,
            status: 'pending',
            coin: normalizedCoinKey,
            amount,
            accountType,
            direction,
            txHash: txHash?.trim() ? txHash.trim() : null,
            note,
            destinationAddress: destinationAddress?.trim?.() || null,
            timestamp,
            processed: false,
            processedAt: null
          };

          const updatedTransactions = [newTransaction, ...existingTransactions].slice(0, 100);

          const updates = {
            transactions: updatedTransactions,
            updatedAt: serverTimestamp()
          };

          // Deposits are now pending - balance updated only after admin approval
          // No auto-balance update for deposits

          transaction.set(
            userRef,
            updates,
            { merge: true }
          );

          createdTransaction = newTransaction;
        });

        // Log deposit requests for admin approval
        if (createdTransaction && createdTransaction.type === 'deposit') {
          const depositRef = doc(
            collection(db, 'depositRequests'),
            createdTransaction.id
          );

          try {
            await setDoc(
              depositRef,
              {
                userId: currentUser.uid,
                userEmail: currentUser.email || null,
                coin: createdTransaction.coin,
                amount: createdTransaction.amount,
                accountType: createdTransaction.accountType,
                status: createdTransaction.status,
                createdAt: createdTransaction.timestamp,
                note: createdTransaction.note || null,
                txHash: createdTransaction.txHash || null
              },
              { merge: true }
            );
          } catch (writeError) {
            console.error('Failed to log deposit request:', writeError);
          }
        }

        // Log withdrawal requests for admin approval
        if (
          createdTransaction &&
          createdTransaction.type === 'withdrawal' &&
          createdTransaction.destinationAddress
        ) {
          const withdrawalRef = doc(
            collection(db, 'withdrawalRequests'),
            createdTransaction.id
          );

          try {
            await setDoc(
              withdrawalRef,
              {
                userId: currentUser.uid,
                userEmail: currentUser.email || null,
                coin: createdTransaction.coin,
                amount: createdTransaction.amount,
                accountType: createdTransaction.accountType,
                destinationAddress: createdTransaction.destinationAddress,
                status: createdTransaction.status,
                createdAt: createdTransaction.timestamp,
                note: createdTransaction.note || null,
                txHash: createdTransaction.txHash || null
              },
              { merge: true }
            );
          } catch (writeError) {
            console.error('Failed to log withdrawal request:', writeError);
          }
        }

        return true;
      } catch (error) {
        console.error('Error creating pending transaction:', error);
        setError('Failed to create pending transaction');
        return false;
      }
    },
    [currentUser, balanceData, getCoinKey]
  );

  const updateSingleBalance = useCallback(
    (coin, amount, accountType = 'trading', note = '') =>
      updateBalance(coin, amount, accountType, note),
    [updateBalance]
  );

  const getBalance = useCallback((coin, accountType = 'trading') => {
    if (!coin) return 0;
    const coinKey = getCoinKey(coin, balanceData.accountBalances[accountType]);
    return balanceData.accountBalances?.[accountType]?.[coinKey] || 0;
  }, [balanceData, getCoinKey]);

  const getAccountBalances = useCallback((accountType = 'trading') => {
    return { ...(balanceData.accountBalances?.[accountType] || {}) };
  }, [balanceData]);

  const getTotalBalance = useCallback((coin) => {
    if (!coin) return 0;
    const coinKey = getCoinKey(coin, balanceData.accountBalances?.trading);
    const trading = balanceData.accountBalances?.trading?.[coinKey] || 0;
    const arbitrage = balanceData.accountBalances?.arbitrage?.[coinKey] || 0;
    return trading + arbitrage;
  }, [balanceData, getCoinKey]);

  const transferBetweenAccounts = useCallback(async (fromAccount, toAccount, coin, amount, note = '') => {
    setError(null);
    setIsLoading(true);

    if (!currentUser?.uid) {
      setError('User not authenticated');
      setIsLoading(false);
      return false;
    }

    // Basic validation
    if (!fromAccount || !toAccount || !coin || !amount || amount <= 0) {
      setError('Invalid transfer parameters');
      setIsLoading(false);
      return false;
    }

    if (fromAccount === toAccount) {
      setError('Cannot transfer to the same account');
      setIsLoading(false);
      return false;
    }

    const userRef = doc(db, 'userProfiles', currentUser.uid);
    
    try {
      // Get current user data
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        throw new Error('User account not found');
      }

      const userData = userDoc.data();
      const rawCoinKey = getCoinKey(
        coin,
        {
          ...(userData.accountBalances?.[fromAccount] || {}),
          ...(userData.accountBalances?.[toAccount] || {})
        }
      ) || coin.toUpperCase();
      const normalizedCoinKey = rawCoinKey.toUpperCase();
      const coinKeys = Array.from(new Set([normalizedCoinKey, rawCoinKey])).filter(Boolean);

      // Get current balances
      const fromBalance = Number(
        coinKeys.reduce((acc, key) => (
          acc !== undefined ? acc : userData.accountBalances?.[fromAccount]?.[key]
        ), undefined) ?? 0
      );
      const toBalance = Number(
        coinKeys.reduce((acc, key) => (
          acc !== undefined ? acc : userData.accountBalances?.[toAccount]?.[key]
        ), undefined) ?? 0
      );
      
      // Validate sufficient balance
      if (fromBalance < amount) {
        throw new Error(`Insufficient ${normalizedCoinKey} balance in ${fromAccount} account`);
      }

      // Calculate new balances
      const newFromBalance = fromBalance - amount;
      const newToBalance = toBalance + amount;

      // Create transaction record
      const now = Timestamp.now();
      const transactionRecord = {
        id: `transfer-${Date.now()}`,
        type: 'transfer',
        status: 'completed',
        coin: normalizedCoinKey,
        amount,
        fromAccount,
        toAccount,
        direction: 'internal-transfer',
        timestamp: now,
        note: note || `Transfer from ${fromAccount} to ${toAccount}`,
        createdAt: now,
        processed: true,
        processedAt: now
      };

      // Prepare updates
      const updates = {
        updatedAt: serverTimestamp()
      };

      coinKeys.forEach((key) => {
        if (key) {
          updates[`accountBalances.${fromAccount}.${key}`] = newFromBalance;
          updates[`accountBalances.${toAccount}.${key}`] = newToBalance;
        }
      });

      // Get existing transactions or initialize empty array
      const currentTransactions = Array.isArray(userData.transactions) 
        ? userData.transactions 
        : [];

      // Update the document with batched writes
      const batch = writeBatch(db);
      batch.update(userRef, updates);
      batch.update(userRef, {
        transactions: [transactionRecord, ...currentTransactions].slice(0, 1000)
      });
      
      await batch.commit();

      // Update local state
      setBalanceData(prev => {
        const updatedFrom = { ...(prev.accountBalances[fromAccount] || {}) };
        const updatedTo = { ...(prev.accountBalances[toAccount] || {}) };
        coinKeys.forEach((key) => {
          if (key) {
            updatedFrom[key] = newFromBalance;
            updatedTo[key] = newToBalance;
          }
        });

        return {
          ...prev,
          accountBalances: {
            ...prev.accountBalances,
            [fromAccount]: updatedFrom,
            [toAccount]: updatedTo
          },
          transactions: [transactionRecord, ...(prev.transactions || [])].slice(0, 1000)
        };
      });

      return true;
      
    } catch (error) {
      console.error('Transfer error:', error);
      setError(error.message || 'Failed to process transfer');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, getCoinKey]);

  const processConfirmedTransactions = useCallback(
    async (transactionsList) => {
      if (!currentUser || !Array.isArray(transactionsList)) {
        return;
      }

      const needsProcessing = transactionsList.some((item) => {
        if (!item || item.processed) {
          return false;
        }

        const status = (item.status || '').toLowerCase();
        return status === 'completed' || status === 'confirmed';
      });

      if (!needsProcessing) {
        return;
      }

      const userRef = doc(db, 'userProfiles', currentUser.uid);

      try {
        await runTransaction(db, async (transaction) => {
          const docSnap = await transaction.get(userRef);
          if (!docSnap.exists()) {
            throw new Error('User profile not found');
          }

          const userData = docSnap.data() || {};
          const existingTransactions = Array.isArray(userData.transactions) ? userData.transactions : [];

          const accountBalances = { ...(userData.accountBalances || {}) };
          Object.keys(DEFAULT_BALANCES.accountBalances).forEach((accountKey) => {
            accountBalances[accountKey] = {
              ...(DEFAULT_BALANCES.accountBalances[accountKey] || {}),
              ...(accountBalances[accountKey] || {})
            };
          });

          const processedTimestamp = Timestamp.fromDate(new Date());

          const updatedTransactions = existingTransactions.map((record) => {
            if (record?.type === 'adjustment') {
              return {
                ...record,
                processed: true,
                processedAt: record.processedAt || processedTimestamp
              };
            }

            const status = (record.status || '').toLowerCase();
            const isConfirmedStatus = status === 'completed' || status === 'confirmed';

            if (isConfirmedStatus && !record.processed) {
              const accountType = record.accountType || 'trading';
              accountBalances[accountType] = {
                ...(DEFAULT_BALANCES.accountBalances[accountType] || {}),
                ...(accountBalances[accountType] || {})
              };

              const coinKey = getCoinKey(record.coin, accountBalances[accountType]);
              const currentAmount = Number(accountBalances[accountType][coinKey] || 0);
              const amountValue = Math.abs(Number(record.amount) || 0);
              const isIncoming = (record.direction || 'incoming') === 'incoming';

              const updatedAmount = isIncoming
                ? currentAmount + amountValue
                : Math.max(currentAmount - amountValue, 0);

              accountBalances[accountType][coinKey] = Number(updatedAmount.toFixed(8));

              return {
                ...record,
                processed: true,
                processedAt: processedTimestamp
              };
            }

            return record;
          });

          transaction.set(
            userRef,
            {
              accountBalances,
              transactions: updatedTransactions,
              updatedAt: serverTimestamp()
            },
            { merge: true }
          );
        });
      } catch (error) {
        setError('Failed to process confirmed transactions');
      }
    },
    [currentUser, getCoinKey]
  );

  const value = useMemo(
    () => ({
      balanceData,
      isLoading,
      error,
      updateBalance,
      applyBalanceDelta,
      updateSingleBalance,
      getBalance,
      getAccountBalances,
      getTotalBalance,
      transferBetweenAccounts,
      createPendingTransaction,
      processConfirmedTransactions
    }),
    [
      balanceData,
      isLoading,
      error,
      updateBalance,
      applyBalanceDelta,
      updateSingleBalance,
      getBalance,
      getAccountBalances,
      getTotalBalance,
      transferBetweenAccounts,
      createPendingTransaction,
      processConfirmedTransactions
    ]
  );

  // ── Withdrawal status sync ──────────────────────────────────────────────
  // When admin changes withdrawalRequests/{id}.status in Firebase console
  // (to confirmed / rejected / cancelled), mirror that change into
  // userProfiles/{uid}.transactions[] so the UI reflects it immediately.
  useEffect(() => {
    if (!currentUser?.uid) return;

    const q = query(
      collection(db, 'withdrawalRequests'),
      where('userId', '==', currentUser.uid)
    );

    const unsub = onSnapshot(q, async (snapshot) => {
      const modified = snapshot.docChanges().filter(c => c.type === 'modified');
      for (const change of modified) {
        const data = change.doc.data();
        const newStatus = data.status;
        const requestId = change.doc.id;

        if (!['confirmed', 'rejected', 'cancelled'].includes(newStatus)) continue;

        try {
          const userRef = doc(db, 'userProfiles', currentUser.uid);
          const userSnap = await getDoc(userRef);
          if (!userSnap.exists()) continue;

          const allTxs = [...(userSnap.data().transactions || [])];
          const idx = allTxs.findIndex(tx => tx.id === requestId);
          if (idx === -1 || allTxs[idx].status === newStatus) continue;

          allTxs[idx] = { ...allTxs[idx], status: newStatus };
          await updateDoc(userRef, { transactions: allTxs, updatedAt: serverTimestamp() });
        } catch (err) {
          console.error('Failed to sync withdrawal status:', err);
        }
      }
    });

    return () => unsub();
  }, [currentUser?.uid]);
  // ── End withdrawal sync ─────────────────────────────────────────────────

  useEffect(() => {
    processConfirmedTransactions(balanceData.transactions);
  }, [balanceData.transactions, processConfirmedTransactions]);

  return (
    <BalanceContext.Provider value={value}>
      {children}
    </BalanceContext.Provider>
  );
};

export const useBalance = () => {
  const context = useContext(BalanceContext);
  if (context === undefined) {
    throw new Error('useBalance must be used within a BalanceProvider');
  }
  return context;
};

export default BalanceContext;
